import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { PAYMENT_STATUSES, label } from "@/lib/constants";
import { Card, PageHeader, Btn, StatusBadge, smInput, Empty } from "@/components/common";
import { Modal } from "@/admin/Dialogs";
import { useAuth } from "@/context/AuthContext";

export function Tasks() {
  const { can } = useAuth();
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", due_date: "", kind: "follow_up", customer_id: "", assigned_to: "", notes: "" });
  const load = () => api.get("/admin/tasks", { params: { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  const create = () => api.post("/admin/tasks", { ...f, customer_id: f.customer_id || null, assigned_to: f.assigned_to || null }).then(() => { setOpen(false); load(); toast.success("Task created"); }).catch((e) => toast.error(errMsg(e)));
  const done = (t) => api.patch(`/admin/tasks/${t.id}`, { done: true }).then(load);
  const names = Object.fromEntries(users.map((u) => [u.id, u.name]));
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="Tasks for calls, deliveries and collections"><Btn size="sm" onClick={() => setOpen(true)} data-testid="add-task-button"><Plus className="h-4 w-4" />New task</Btn></PageHeader>
      <div className="mb-4 flex gap-2">{["open", "done", "all"].map((s) => <Btn key={s} size="sm" variant={status === s ? "dark" : "outline"} onClick={() => setStatus(s)} data-testid={`task-filter-${s}`}>{s}</Btn>)}</div>
      <Card className="divide-y divide-slate-100">
        {items.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 p-4" data-testid={`task-row-${t.id}`}>
            <div>
              <p className={`font-semibold ${t.done ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</p>
              <p className="text-xs text-slate-500">{t.kind.replace("_", " ")} · due <span className={!t.done && t.due_date < today ? "font-bold text-rose-600" : ""}>{fmtDate(t.due_date)}</span> · {names[t.assigned_to] || "—"}{t.customer_id && <> · <Link to={`/admin/customers/${t.customer_id}`} className="text-emerald-800">{t.customer_name}</Link></>}</p>
            </div>
            <div className="flex gap-2">{!t.done && <Btn size="sm" variant="outline" onClick={() => done(t)} data-testid={`task-done-${t.id}`}><Check className="h-4 w-4" />Done</Btn>}{can("records:delete") && <Btn size="sm" variant="ghost" onClick={() => window.confirm("Delete this task?") && api.delete(`/admin/tasks/${t.id}`).then(load)} data-testid={`task-delete-${t.id}`}>Delete</Btn>}</div>
          </div>
        ))}
        {!items.length && <Empty>No tasks.</Empty>}
      </Card>
      <Modal open={open} onClose={() => setOpen(false)} title="New follow-up task">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" data-testid="task-title-input" className={`${smInput} w-full`} />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input" className={smInput} />
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={smInput}>{["follow_up", "call", "email", "text", "delivery", "collection", "other"].map((k) => <option key={k}>{k}</option>)}</select>
          <input value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value.trim() })} placeholder="Customer ID (optional)" className={smInput} />
          <select value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} className={smInput}><option value="">Assign to me</option>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
        </div>
        <Btn disabled={f.title.length < 2 || !f.due_date} onClick={create} data-testid="task-save-button">Create task</Btn>
      </Modal>
    </div>
  );
}

export function Payments() {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments", { params: { status } }).then((r) => setItems(r.data)); }, [status]);
  const total = items.filter((p) => p.status === "succeeded").reduce((s, p) => s + p.amount, 0);
  return (
    <div>
      <PageHeader title="Payments" subtitle="Manual payment ledger — stores card brand + last 4 only, never full card details">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payments-filter-status" className={smInput}><option value="">All statuses</option>{PAYMENT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      </PageHeader>
      <Card className="mb-4 border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="payments-test-mode-notice">
        <b>Stripe (TEST mode).</b> Customers can save a card on Stripe's secure page when booking (we only keep brand, last 4 and expiry — never the full number or CVV). From a confirmed booking staff can charge the saved card or send a Stripe payment link. Charging saved cards directly and going live require your own Stripe account keys. Manual entries (terminal / cash) can still be recorded.
      </Card>
      <p className="mb-3 text-sm text-slate-600">Successful in view: <b className="text-emerald-700">{money(total)}</b></p>
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm" data-testid="payments-table">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Payment", "Booking", "Customer", "Amount", "Type / method", "Card", "Transaction ID", "Status", "Date"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((p) => (
              <tr key={p.id}>
                <td className="px-3 py-3 font-mono text-xs">{p.id}</td>
                <td className="px-3 py-3"><Link to={`/admin/bookings/${p.booking_id}`} className="font-mono text-emerald-800">{p.booking_reference}</Link></td>
                <td className="px-3 py-3">{p.customer_name}</td>
                <td className="px-3 py-3 font-semibold">{money(p.amount)}</td>
                <td className="px-3 py-3">{p.kind.replace("_", " ")} · {p.method}</td>
                <td className="px-3 py-3">{p.card_last4 ? `${p.card_brand} •••• ${p.card_last4}` : "—"}</td>
                <td className="px-3 py-3 font-mono text-xs">{p.transaction_id || "—"}</td>
                <td className="px-3 py-3"><StatusBadge value={p.status} text={label(PAYMENT_STATUSES, p.status)} /></td>
                <td className="px-3 py-3">{fmtDateTime(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <Empty>No payments recorded.</Empty>}
      </Card>
    </div>
  );
}

export function Notifications() {
  const [d, setD] = useState({ items: [], logs: [] });
  const load = () => api.get("/admin/notifications").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Notifications" subtitle="In-dashboard alerts and delivery log for email notifications"><Btn size="sm" variant="outline" onClick={() => api.post("/admin/notifications/read-all").then(load)} data-testid="notifications-read-all">Mark all read</Btn></PageHeader>
      <Card className="mb-6 divide-y divide-slate-100">
        {d.items.map((n) => (
          <Link key={n.id} to={n.link || "#"} onClick={() => api.post(`/admin/notifications/${n.id}/read`)} className={`flex items-start justify-between gap-3 p-4 hover:bg-slate-50 ${n.read ? "" : "bg-emerald-50/50"}`} data-testid={`notification-${n.id}`}>
            <div><p className="font-semibold text-slate-900">{!n.read && <span className="mr-2 inline-block h-2 w-2 rounded-full bg-amber-500" />}{n.title}</p><p className="text-sm text-slate-600">{n.message}</p></div>
            <span className="shrink-0 text-xs text-slate-500">{fmtDateTime(n.created_at)}</span>
          </Link>
        ))}
        {!d.items.length && <Empty>No notifications yet.</Empty>}
      </Card>
      {d.logs.length > 0 && (
        <Card className="overflow-x-auto">
          <p className="p-4 font-display font-semibold">Notification delivery log</p>
          <table className="w-full text-sm" data-testid="notification-logs-table"><thead className="border-y bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{["When", "Channel", "Purpose", "Recipient", "Status", "Detail"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">{d.logs.map((l) => <tr key={l.id}><td className="px-3 py-2 text-xs">{fmtDateTime(l.created_at)}</td><td className="px-3 py-2">{l.channel}</td><td className="px-3 py-2">{l.purpose}</td><td className="px-3 py-2">{l.recipient}</td><td className="px-3 py-2"><StatusBadge value={l.status} text={l.status.replace("_", " ")} /></td><td className="px-3 py-2 text-xs text-slate-500">{l.detail}</td></tr>)}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
